import type { OfflineNotification } from '@/lib/stores/lobbyStore.types';

/** A genuine rejection for the in-flight transition always arrives within a
 * couple seconds over the open WS; anything staler is leftover from an
 * earlier, unrelated attempt. */
export const STALE_REJECTION_WINDOW_MS = 15_000;

/**
 * Find the freshest unread "Lobby Cancelled" notification, if any is still
 * within the staleness window. This title is reused by both an actual lobby
 * cancellation AND `lobby:start-game-result` rejections (party-broadcast, so
 * non-requesting party members receive it too) AND ChatPanel's
 * `/start-adventure` (which never sets `isGameTransitioning`). None of these
 * notifications ever auto-expire, so without this recency check a stale
 * unread one from an earlier, unrelated attempt could wrongly cancel a
 * brand-new transition the moment `isGameTransitioning` next turns true.
 *
 * Lives in a standalone module (not LobbyLayout.tsx) so the unit test can
 * import it without dragging React + the full lobby component graph into the
 * test harness.
 */
export function findFreshCancelledNotification(
  notifications: OfflineNotification[],
  now: number
): OfflineNotification | undefined {
  return notifications.find(
    (n) =>
      n.type === 'system' &&
      n.title === 'Lobby Cancelled' &&
      !n.isRead &&
      now - n.timestamp.getTime() < STALE_REJECTION_WINDOW_MS
  );
}
